/** lag-tags-input
 * A listing that has tags. It provides ality to add and remove tags as needed.
 * 
 * REFERENCE: https://github.com/yairEO/tagify/
 *
 * PROPERTIES/METHODS: {
 *   placeholder: Input box placeholder.
 *   whiteList: A collection of tags that are permitted and will show on autocomplete.
 *   hasChanged: Indicates if tags have changed since startup / reset.
 *   tags: The current tags.
 * }
 */

import { PolymerElement } from '@polymer/polymer/polymer-element.js';

import Tagify from './lag-tags-input.tagify.js';

import { defineElement } from '../../utility/defineElement.js';
import { template } from './lag-tags-input.html.js';

const DROPDOWN_MIN_CHARS = 1; // If enabled then show dropdown when only 1 char entered.

defineElement(class extends PolymerElement {

    static get is() { return 'lag-tags-input'; }

    static get template() { return template; }

    static get properties() {
        return {
            placeholder: {
                type: String,
                value: null,
                observer: '_observePlaceholder'
            },
            whitelist: {
                type: Array,
                value: null,
                observer: '_observeWhitelist'
            },
            hasChanged: {
                type: Boolean,
                value: false,
                readOnly: true,
                notify: true
            },
            tags: {
                type: Array,
                value: null,
                readOnly: true,
                notify: true
            }
        };
    }

    ready() {
        super.ready();

        this.tagify = new Tagify(this.$.input, {
            placeholder: this.placeholder,
            whitelist: this.whitelist || [],
            enforceWhitelist: this._isWhitelistEnforceable(this.whitelist),
            skipInvalid: true,
            dropdown: {
                enabled: false, // DROPDOWN_MIN_CHARS,
                position: 'manual', // Handle placing the suggestions ourself, otherwise it adds it to body and screws stuff up.
                classname: 'tagify__dropdown',
                closeOnSelect: true
            }
        });


        //this.$.dropdown.appendChild(this.tagify.DOM.dropdown);

        this._setHasChanged(false);

        this.tagify
            .on('add', ev => this._handleTagsUpdated(ev) && this._handleDropdownHide(ev))
            .on('edit', ev => this._handleTagsUpdated(ev) && this._handleDropdownHide(ev))
            .on('remove', ev => this._handleTagsUpdated(ev) && this._handleDropdownHide(ev))
            .on('dropdown:show', ev => this._handleDropdownShow(ev))
            .on('blur', ev => this._handleDropdownHide(ev))
            .on('dropdown:select', ev => this._handleDropdownSelect(ev));
    }

    // == OBSERVERS ==

    _observePlaceholder(placeholder, oldPlaceholder) {
        if (!this.tagify || typeof placeholder !== 'string' || placeholder === oldPlaceholder)
            return;

        this.tagify.settings.placeholder = placeholder;
    }

    _observeWhitelist(whitelist, oldWhitelist) {
        if (!this.tagify || !whitelist || whitelist === oldWhitelist)
            return;

        this.tagify.settings.whitelist = whitelist || [];
        this.tagify.settings.enforceWhitelist = this._isWhitelistEnforceable(whitelist);
    }

    /*_observeEnableDropdown(enableDropdown, oldEnableDropdown) {
        if (!this.tagify || typeof enableDropdown === 'undefined' || enableDropdown === oldEnableDropdown)
            return;

        this.tagify.settings.dropdown.enabled = enableDropdown ? DROPDOWN_MIN_CHARS : false;
    }*/

    // == HELPERS ==

    _isWhitelistEnforceable(whitelist) { return !!(whitelist && whitelist.length); }

    _showDropdown(show) {
        if (this.$.dropdown.hidden === !show)
            return;

        this.$.dropdown.hidden = !show;

        if (show)
            this.tagify.dropdown.show.call(this.tagify);
        else
            this.tagify.dropdown.hide.call(this.tagify);
    }

    // == PUBLIC METHODS ==

    resetTags(tags) {
        this.clearTags();
        if (tags) this.addTags(tags);
        this._setHasChanged(false);
    }

    clearTags() {
        this.tagify.removeAllTags();
        this.tags = null;
        this._setHasChanged(true);
    }

    addTags(tags) {
        this.tagify.addTags(tags);
        this._setHasChanged(true);
    }

    // == EVENT HANDLERS ==

    _handleTagsUpdated() {
        // Sync this.tags with tagify.value (tags).
        this._setTags(this.tagify.value);
        this._setHasChanged(true);
    }

    _handleDropdownShow(ev) {
        this._showDropdown(true);
    }

    _handleDropdownHide(ev) {
        this._showDropdown(false);
    }

    _handleDropdownSelect(ev) {
    }

});